import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

// Actions
import { toggleMediaType } from "../../actions/campaign-planning/CampaignPlanningActions";

// Constants and Utils
import { OrgCityConfigTypes } from "../../../constants/GeneralConstants";
import { getPlanningQueryParams } from "./CampaignPlanningUtils";

// Components
import CheckBox from "../../../components/check-box/CheckBox";
import { DataLoading, HeaderWithActionButton } from "./PlanningUtils";

// get Media Types Selected Count
function getMediaTypesSelectedCount(types = [], selectedMediaTypes = []) {
  if (selectedMediaTypes.length === 0) {
    return 0;
  }

  const mediaTypesSelected = selectedMediaTypes.filter((mediaType) =>
    types.includes(mediaType)
  );
  return mediaTypesSelected.length;
}

function getFinalMediaTypeGroups(mediaTypesGroups, isPureDooh) {
  // for pureDooh, only showing mediaTypes of Digital
  if (isPureDooh) {
    return ["Digital"];
  }

  // return both [static & digital]
  return mediaTypesGroups;
}

function MediaGroupSection({
  selectedMediaGroup,
  setSelectedMediaGroup = () => {},
  selectedMediaTypes = [],
  mediaTypesGroupsMap = {},
}) {
  const { search } = useLocation();
  const { isPureDooh } = getPlanningQueryParams(search);

  // media type groups
  const mediaTypesGroups = Object.keys(mediaTypesGroupsMap);
  const finalMediaTypesGroups = getFinalMediaTypeGroups(
    mediaTypesGroups,
    isPureDooh
  );

  useEffect(() => {
    const firstMediaGroup = finalMediaTypesGroups[0] || "";
    setSelectedMediaGroup(firstMediaGroup);
  }, []);

  return (
    <div className="col-6 px-0 border-right">
      <div className="px-3">
        {finalMediaTypesGroups.map((mediaTypeGroup, index) => {
          const groupInfo = mediaTypesGroupsMap[mediaTypeGroup] || {};
          const { label, types = [] } = groupInfo || {};

          const isMediaGroupSelected = selectedMediaGroup === mediaTypeGroup;
          const selectedBgStyle = isMediaGroupSelected
            ? "bg-alt rounded-lg"
            : "";
          const mediaTypesSelectedCount = getMediaTypesSelectedCount(
            types,
            selectedMediaTypes
          );
          const iconClass = isMediaGroupSelected
            ? "fa-angle-down text-primary"
            : "fa-angle-right";
          return (
            <div
              key={index}
              className={`d-flex align-items-center justify-content-between p-2 cursor-pointer my-2 ${selectedBgStyle}`}
              onClick={() => setSelectedMediaGroup(mediaTypeGroup)}
            >
              <p className="mb-0">{label}</p>
              <p className="mb-0">
                {mediaTypesSelectedCount > 0 && (
                  <span className="mr-2">({mediaTypesSelectedCount})</span>
                )}
                <i className={`fa ${iconClass}`} />
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// check Is All Filtered media types are Selected
function checkIsAllFilteredSelected(
  filteredTypes = [],
  selectedMediaTypes = []
) {
  // if no filtered media types present
  if (filteredTypes.length === 0) {
    return false;
  }

  // check condition
  return filteredTypes.every((mediaType) =>
    selectedMediaTypes.includes(mediaType)
  );
}

function MediaTypesCheckboxSection({
  selectedMediaGroup,
  selectedMediaTypes = [],
  appliedFiltersDetailsMap = {},
  mediaTypesGroupsMap = {},
}) {
  const dispatch = useDispatch();

  const selectedMediaGroupInfo = mediaTypesGroupsMap[selectedMediaGroup] || {};
  const { label, types = [] } = selectedMediaGroupInfo || {};

  const {
    appliedFilterOrder = {},
    isArterialRoutesApplied = false,
    isZonesApplied = false,
    firstAppliedFilterOptions = {},
    secondAppliedFilterOptions = {},
  } = appliedFiltersDetailsMap || {};

  const { mediaTags: firstMediaTags = [] } = firstAppliedFilterOptions;
  const { mediaTags: secondMediaTags = [] } = secondAppliedFilterOptions;

  const isFilteredOption = isZonesApplied || isArterialRoutesApplied;
  const isMediaTypeFirstApplied =
    appliedFilterOrder?.firstApplied === "mediaType";
  const isMediaTypeSecondApplied =
    appliedFilterOrder?.secondApplied === "mediaType";
  const isFirstAppliedPresent = Boolean(appliedFilterOrder?.firstApplied);
  const isSecondAppliedPresent = Boolean(appliedFilterOrder?.secondApplied);

  // Filter the media types before rendering
  const filteredTypes = types.filter((mediaType) => {
    if (isFilteredOption && !isMediaTypeFirstApplied) {
      if (isSecondAppliedPresent) {
        if (isMediaTypeSecondApplied && !firstMediaTags.includes(mediaType)) {
          return false;
        }
        if (!isMediaTypeSecondApplied && !secondMediaTags.includes(mediaType)) {
          return false;
        }
      }

      if (isFirstAppliedPresent && !isMediaTypeFirstApplied) {
        if (!firstMediaTags.includes(mediaType)) {
          return false;
        }
      }
    }
    return true;
  });

  // check all filtered option selected or not
  const isAllFilteredSelected = checkIsAllFilteredSelected(
    filteredTypes,
    selectedMediaTypes
  );

  // function
  function handleCheckboxChange(mediaType) {
    dispatch(toggleMediaType([mediaType]));
  }

  function handleSelectAllCheckboxChange() {
    if (isAllFilteredSelected) {
      // Deselect all media types
      filteredTypes.forEach((mediaType) => {
        dispatch(toggleMediaType([mediaType]));
      });
      return;
    }

    // Select all media types
    filteredTypes.forEach((mediaType) => {
      if (!selectedMediaTypes.includes(mediaType)) {
        dispatch(toggleMediaType([mediaType]));
      }
    });
  }

  return (
    <div className="col-6 px-0 border-left">
      <div className="d-flex align-items-center justify-content-between px-3">
        <h5 className="my-1 py-2">{label}</h5>
        <CheckBox
          boxStyle="mb-0"
          showLabel={false}
          checked={isAllFilteredSelected}
          onChange={handleSelectAllCheckboxChange}
        />
      </div>
      <hr className="my-0" />

      {filteredTypes.length === 0 ? (
        <p className="px-3 py-2 text-muted">No media types</p>
      ) : (
        filteredTypes.map((mediaType, index) => {
          const isChecked = selectedMediaTypes.includes(mediaType);
          const elementKey = `${mediaType}-${index}`;
          return (
            <div
              key={elementKey}
              className="d-flex justify-content-between mt-1 px-3 py-1"
            >
              <p className="mb-0" title={mediaType}>
                {mediaType}
              </p>
              <CheckBox
                boxStyle="mb-0"
                checkBoxKey={mediaType}
                showLabel={false}
                checked={isChecked}
                onChange={() => handleCheckboxChange(mediaType)}
              />
            </div>
          );
        })
      )}
    </div>
  );
}

// Media Types Checkbox List
function MediaTypesCheckboxList({
  selectedMediaTypes = [],
  appliedFiltersDetailsMap = {},
}) {
  const [selectedMediaGroup, setSelectedMediaGroup] = useState("");

  // city config
  const cityConfig = useSelector((state) => state.orgCityConfig.cityConfig);
  const cityConfigLoading = useSelector(
    (state) => state.orgCityConfig.cityConfigLoading
  );

  // mediaTypesGroups map
  const mediaTypesGroupsMap = cityConfig[OrgCityConfigTypes.mediaType] || {};

  // loading
  if (cityConfigLoading) {
    return <DataLoading dataType="Media Types" />;
  }

  return (
    <div className="w-100 d-flex border rounded-lg overflow-hidden">
      {/* left section */}
      <MediaGroupSection
        selectedMediaGroup={selectedMediaGroup}
        setSelectedMediaGroup={setSelectedMediaGroup}
        selectedMediaTypes={selectedMediaTypes}
        mediaTypesGroupsMap={mediaTypesGroupsMap}
      />

      {/* Checkbox Section */}
      {selectedMediaGroup && (
        <MediaTypesCheckboxSection
          selectedMediaGroup={selectedMediaGroup}
          selectedMediaTypes={selectedMediaTypes}
          appliedFiltersDetailsMap={appliedFiltersDetailsMap}
          mediaTypesGroupsMap={mediaTypesGroupsMap}
        />
      )}
    </div>
  );
}

// Media Types Tab
export default function MediaTypesTab({
  handleTabHeaderBtnClick = () => {},
  tabHeaderBtnLoading = false,
  appliedFiltersDetailsMap = {},
}) {
  // Selected Media Types Map
  const selectedMediaTypesMap = useSelector(
    (state) => state.campaignPlanning.selectedMediaTypes
  );
  const selectedMediaTypes = Object.keys(selectedMediaTypesMap).filter(
    (type) => selectedMediaTypesMap[type]
  );

  return (
    <>
      <HeaderWithActionButton
        title="Media Types"
        buttonText="Confirm"
        onButtonClick={handleTabHeaderBtnClick}
        buttonLoading={tabHeaderBtnLoading}
      />

      <MediaTypesCheckboxList
        selectedMediaTypes={selectedMediaTypes}
        appliedFiltersDetailsMap={appliedFiltersDetailsMap}
      />
    </>
  );
}
